import { makeObservable, toJS, runInAction, action, observable } from "mobx";
import { PerPageSettings } from "./core/PerPageSettings";
import organizationsApi from "../api/OrgStructure";
import ApiNsi from "../modules/api/Nsi";
import ApiOrgStructure from "../api/OrgStructure";
import { ExpandableKeys, Filter } from "./core";
import { Search, SearchRequest } from "../api/Types";
import Sorter from "./core/sorter";
import { StatusInfo } from "./core/StatusInfo";
import Constants from "../constants/Common";

class NsiOrganizationsStore {
  filter = new Filter();
  firstOrgNumber = 1;
  ftg = []; // функционально-топологическая группа
  grbsArray = [];
  gridTypesList = [];
  formsSubmitLoading = {};
  openNewOrgForm = false;
  organizations = [];
  buildings = [];
  typesRGBS = [];
  organizationRowKeys: ExpandableKeys = new ExpandableKeys();
  buildingsLoading = false;
  organizationsLoading = false;
  ownershipTypeList = [];
  paginator = new PerPageSettings(1, 10);
  search: Search = {
    text: "",
    fieldNames: ["name"],
    allTextFields: true,
  };
  sorter = new Sorter();
  messageStatus = new StatusInfo();

  constructor() {
    makeObservable(
      this,
      {
        updateOrganization: action,
        fetchOrganizations: action,
        getBuildingsByOrg: action,
        organizations: observable,
        buildings: observable,
        typesRGBS: observable,
      },
      {
        deep: true,
      },
    );
    this.sorter.addSortItem("name", 0);
  }

  getBuildingsByOrg = async (id) => {
    try {
      if (!id) {
        this.buildings = [];
        return;
      }

      const { data } = await organizationsApi.getBuildingsByOrg(id);
      runInAction(() => {
        this.buildings = data;
      });
    } catch (error) {
      console.log(error);
    }
  }

  deleteOrganization = async (id) => {
    try {
      await ApiOrgStructure.deleteOrg(id);
      runInAction(() => {
        this.messageStatus.setStatus(Constants.requestStatuses.deleted);
        this.organizations = this.organizations.filter((org) => org.id !== id);
      });
    } catch (err) {
      this.messageStatus.setDetails(err.response.data.errors);
      this.messageStatus.setStatus(Constants.requestStatuses.error);
    }
  };

  deleteBuilding = async (id) => {
    try {
      await ApiOrgStructure.deleteBuilding(id);
      runInAction(() => {
        this.messageStatus.setStatus(Constants.requestStatuses.deleted);
      });
      return true;
    } catch (err) {
      this.messageStatus.setDetails(err);
      this.messageStatus.setStatus(Constants.requestStatuses.error);
      return false;
    }
  };

  fetchOrganizations(
    params = { organizationsLoading: true, buildingsLoading: false },
  ) {
    if (params?.buildingsLoading) {
      this.buildingsLoading = true;
    }
    if (params?.organizationsLoading) {
      this.organizationsLoading = true;
    }

    const request: SearchRequest = {
      page: this.paginator.page,
      count: this.paginator.perPage,
      sortItems: this.sorter.sortData,
      filterItems: this.filter.filterItems,
      search: this.search,
    };

    organizationsApi.getOrganizationsWithBuildings(request).then((res) => {
      runInAction(() => {
        this.organizations = res.data.items.map((item) => {
          item.rowKeys = new ExpandableKeys();
          return item;
        });
        this.firstOrgNumber = res.data.firstItemNumber;
        this.organizationsLoading = false;
        this.buildingsLoading = false;
        this.paginator.updateMeta(res.data);
      });
    });
  }
  getTypeRGBS = async () => {
    try {
      const { data } = await ApiNsi.getTypeGRBS({ page: 1, count: 0 });
      runInAction(() => {
        this.typesRGBS = data.items;
      });
    } catch (error) {
      console.log("NsiOrganizationsStore.getTypeRGBS error:", error);
      console.log(error);
    }
  };

  getFtg = async () => {
    try {
      const { data } = await ApiNsi.getFtg();
      runInAction(() => {
        this.ftg = data.items;
      });
    } catch (error) {
      console.log("NsiOrganizationsStore.getFtg error:", error);
      console.log(error);
    }
  };

  getGrbsOrgs = async () => {
    try {
      const { data } = await ApiNsi.getBudgetOrgs();

      runInAction(() => {
        this.grbsArray = data.items;
      });
    } catch (error) {
      runInAction(() => {
        console.log("NsiOrganizationsStore.getGrbsOrgs error:", error);
      });
    }
  };

  getGridTypes = async () => {
    try {
      const { data } = await ApiNsi.getGridTypes();
      runInAction(() => {
        this.gridTypesList = data.items;
      });
    } catch (error) {
      console.log("NsiOrganizationsStore.getGridTypes error:", error);
    }
  };

  getMessageDetails(err): Array<string> {
    let errors;

    if (err.response.data.status == 500) {
      errors = [err.response.data.title];
    } else {
      errors = err.response.data.errors;
    }

    if (typeof errors === "object" && !Array.isArray(errors)) {
      return Object.values(errors);
    }

    return errors;
  }

  getOwnershipTypes = async () => {
    try {
      const { data } = await ApiNsi.getOwnershipTypes();
      runInAction(() => {
        this.ownershipTypeList = data.items;
      });
    } catch (error) {
      console.log("NsiOrganizationsStore.getOwnershipTypes error:", error);
    }
  };

  updateOrganization = async (data) => {
    this.formsSubmitLoading[data.id] = true;
    try {
      if (data.id) {
        await ApiOrgStructure.updateOrg(data);
        runInAction(() => {
          this.messageStatus.setStatus(Constants.requestStatuses.updated);
          this.getGrbsOrgs();
          this.fetchOrganizations();
        });
      } else {
        await ApiOrgStructure.insertOrg(data);
        runInAction(() => {
          this.messageStatus.setStatus(Constants.requestStatuses.created);
          this.getGrbsOrgs();
          this.fetchOrganizations();
          this.openNewOrgForm = false;
        });
      }
      return { successful: true };
    } catch (err) {
      this.messageStatus.setDetails(err);
      this.messageStatus.setStatus(Constants.requestStatuses.error);
      return { successful: false };
    } finally {
      this.formsSubmitLoading[data.id] = false;
    }
  };
  updatefromfile = async (data) => {
    try {
      await ApiOrgStructure.updatefiles(data);
    } catch (e) {
      console.log(e);
    }
  };

  updateBuilding = async (
    data,
    afterSuccess = null,
    addBuildingFromEnergyProfileCallback = null,
  ) => {
    try {
      if (data.id) {
        await ApiOrgStructure.updateBuilding(data);
        if (!addBuildingFromEnergyProfileCallback) {
          runInAction(async () => {
            this.messageStatus.setStatus(Constants.requestStatuses.updated);
            await this.fetchOrganizations({
              buildingsLoading: true,
              organizationsLoading: false,
            });
          });
        }
      } else {
        await ApiOrgStructure.insertBuilding(data);
        if (!addBuildingFromEnergyProfileCallback) {
          runInAction(async () => {
            this.messageStatus.setStatus(Constants.requestStatuses.created);
            await this.fetchOrganizations({
              buildingsLoading: true,
              organizationsLoading: false,
            });
          });
        }
      }
      if (addBuildingFromEnergyProfileCallback) {
        addBuildingFromEnergyProfileCallback();
      }
      if (afterSuccess) {
        afterSuccess();
      }
    } catch (err) {
      this.messageStatus.setDetails(err);
      this.messageStatus.setStatus(Constants.requestStatuses.error);
    }
  };
}

export default new NsiOrganizationsStore();
